import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  parsePhoneNumberFromString as parseMin,
  CountryCode,
} from 'libphonenumber-js';
import { RootState } from '~/store/config';
import log from '~/helpers/log';

/**
 * Formats a phone number for display based on the authenticated user's country.
 *
 * - If the number matches the user's country, returns it in national format (e.g. "085 123 4567").
 * - If the number is from a different country, returns it in international format (e.g. "+44 20 1234 5678").
 * - Numbers passed with a "00" prefix are treated as international (equivalent to "+").
 * - Extension numbers (< 5 digits), non-string values, or unparseable numbers are returned as-is.
 *
 * The user's country is resolved automatically from Redux (`state.auth.userCountry`).
 */
export default function useFormatPhoneNumber() {
  const userCountry = useSelector(
    (state: RootState): string | null | undefined => state.auth.userCountry
  );

  const formatDisplayPhoneNumber = useCallback(
    (number: unknown): unknown => {
      const country = (userCountry || '').toUpperCase();

      try {
        if (typeof number !== 'string') return number;

        // Extension numbers or short codes — return as-is
        if (number.replace(/\D/g, '').length < 5) return number;

        // National format: no international prefix — try to parse with the resolved country
        if (!number.startsWith('00') && !number.startsWith('+')) {
          if (!country) return number;
          const parsed = parseMin(number, country as CountryCode);
          if (!parsed || !parsed.isValid()) return number;
          return parsed
            .formatNational()
            .replace(/[()-]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
        }

        // Normalize 00-prefix to E.164
        const e164 = number.startsWith('00') ? `+${number.slice(2)}` : number;

        const parsed = parseMin(e164);
        if (!parsed || !parsed.isValid()) return number;

        // Same country as the user → national format, otherwise international
        if (country && parsed.country === country) {
          return parsed
            .formatNational()
            .replace(/[()-]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
        }
        return parsed.formatInternational();
      } catch (err) {
        log.error('Failed to format phone number: ', err);
        return number;
      }
    },
    [userCountry]
  );

  return { formatDisplayPhoneNumber };
}
